import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Token is already stored with 'Bearer' prefix, use it directly
        const response = await api.get('/auth/me', {
          headers: { Authorization: token }
        });
        setCurrentUser(response.data);
        localStorage.setItem('user_id', response.data.id.toString());
      } catch (error) {
        console.error('Auth status check failed:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user_id');
        setCurrentUser(null);
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      // Create form data
      const formData = new URLSearchParams();
      formData.append('username', email);  // OAuth2 expects 'username' field
      formData.append('password', password);

      const response = await api.post('/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      
      const { access_token, token_type } = response.data;
      // Store token with proper format
      const token = `Bearer ${access_token}`;
      localStorage.setItem('token', token);
      
      // Fetch user data after successful login
      const userResponse = await api.get('/auth/me', {
        headers: { Authorization: token }
      });
      
      setCurrentUser(userResponse.data);
      localStorage.setItem('user_id', userResponse.data.id.toString());
      setError(null);
      return userResponse.data;
    } catch (error) {
      setError(error.response?.data?.detail || 'Login failed');
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      // Create the user
      const response = await api.post('/users/', userData);
      console.log('User created:', response.data);
      
      // Login to get the token
      const loginFormData = new URLSearchParams();
      loginFormData.append('username', userData.email);
      loginFormData.append('password', userData.password);
      loginFormData.append('grant_type', 'password'); // Add grant_type for OAuth2
      loginFormData.append('scope', ''); // Add empty scope for OAuth2

      console.log('Attempting login with:', userData.email); // Debug log
      console.log('Login form data:', Object.fromEntries(loginFormData)); // Debug log

      const loginResponse = await api.post('/auth/login', loginFormData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      
      console.log('Login response:', loginResponse.data); // Debug log
      
      if (!loginResponse.data.access_token) {
        throw new Error('No access token received from login');
      }

      const { access_token, token_type } = loginResponse.data;
      // Store token with proper format
      const token = `Bearer ${access_token}`;
      localStorage.setItem('token', token);
      console.log('Stored token in localStorage:', token); // Debug log
      
      // Fetch user data with proper Authorization header
      console.log('Fetching user data with token:', token); // Debug log
      const userResponse = await api.get('/auth/me', {
        headers: { Authorization: token }
      });
      console.log('User data response:', userResponse.data); // Debug log
      
      setCurrentUser(userResponse.data);
      setError(null);

      // Return user data with email_verified status
      // Don't auto-redirect - let the Register component show verification message first
      return {
        ...userResponse.data,
        email_verified: response.data.email_verified || false,
        // Don't set redirectTo - let Register component handle it
      };
    } catch (error) {
      console.error('Registration error:', error);
      if (error.response) {
        console.error('Error response:', error.response.data); // Debug log
        console.error('Error status:', error.response.status); // Debug log
        console.error('Error headers:', error.response.headers); // Debug log
      }
      setError(error.response?.data?.detail || 'Registration failed');
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user_id');
    setCurrentUser(null);
    setError(null);
  };

  const updateProfile = async (profileData) => {
    try {
      const token = localStorage.getItem('token');
      // Token is already stored with 'Bearer' prefix, use it directly
      const response = await api.put('/auth/profile', profileData, {
        headers: { Authorization: token }
      });
      setCurrentUser(response.data);
      setError(null);
      return response.data;
    } catch (error) {
      setError(error.response?.data?.message || 'Profile update failed');
      throw error;
    }
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    register,
    logout,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 